// 📁 src/pages/admin/AdminUserManagement.jsx
import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosInstance";
import Papa from "papaparse";
import { useAuth } from "../../context/AuthContext";

const AdminUserManagement = () => {
  useAuth();
  const [activeTab, setActiveTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [proInterests, setProInterests] = useState([]);

  /* ================= ADD USER (ADMIN MANUAL CREATE) ================= */
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "founder",
    plan: "free",
  });

  /* ================= INVESTOR ================= */
  const [newInvestor, setNewInvestor] = useState({
    name: "",
    email: "",
    stage: "",
    sector: "",
    ticketSize: "",
    region: "",
    website: "",
    matchScore: 80,
  });

  /* ================= MENTOR ================= */
  const [newMentor, setNewMentor] = useState({
    name: "",
    email: "",
    phone: "",
    skills: "",
    experience: "",
    bio: "",
    hourlyRate: "",
    status: "pending",
  });

  const [, setEditingRateId] = useState(null);
  const [newRate, setNewRate] = useState("");

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  /* =====================================================
     USERS
  ===================================================== */
  const fetchUsers = async () => {
  try {
    const res = await axios.get("/api/admin/users", authHeader);

    const safeUsers = Array.isArray(res.data)
      ? res.data
      : res.data?.users || [];

    setUsers(safeUsers);
  } catch (err) {
    console.error("❌ Fetch users failed:", err);
  }
};

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ---------- ADMIN ADD USER ---------- */
  const addUser = async () => {
    if (!newUser.name || !newUser.email) {
      alert("Name & Email required");
      return;
    }

    await axios.post("/api/admin/users", newUser, authHeader);
    setNewUser({ name: "", email: "", role: "founder", plan: "free" });
    fetchUsers();
  };

  /* ---------- PLAN CHANGE (SOURCE OF TRUTH = users.plan) ---------- */
  const updatePlan = async (userId, nextPlan) => {
    if (nextPlan === "pro") {
      await axios.post(`/api/admin/users/${userId}/upgrade`, {}, authHeader);
    } else {
      await axios.post(`/api/admin/users/${userId}/downgrade`, {}, authHeader);
    }
    fetchUsers(); // 🔁 hard refresh from DB
  };

  /* ---------- ROLE CHANGE ---------- */
  const updateRole = async (userId, role) => {
    await axios.patch(
      `/api/admin/users/${userId}/role`,
      { role },
      authHeader
    );
    fetchUsers();
  };

  /* ---------- OFFBOARD ---------- */
  const offboardUser = async (userId) => {
    if (!window.confirm("Offboard this user?")) return;
    await axios.patch(
      `/api/admin/users/${userId}/deactivate`,
      {},
      authHeader
    );
    fetchUsers();
  };


  /* =====================================================
     INVESTORS
  ===================================================== */
  const fetchInvestors = async () => {
  try {
    const res = await axios.get("/api/admin/investors", authHeader);

    setInvestors(
      Array.isArray(res.data)
        ? res.data
        : res.data?.investors || []
    );
  } catch (err) {
    console.error("❌ Fetch investors failed:", err);
  }
};

  useEffect(() => {
    if (activeTab === "investors") fetchInvestors();
  }, [activeTab]);

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      complete: async ({ data }) => {
        const clean = data.filter(i => i.name && i.stage);
        await axios.post(
          "/api/admin/investors/import",
          { investors: clean },
          authHeader
        );
        fetchInvestors();
      },
    });
  };

  
  const addInvestor = async () => {
    if (!newInvestor.name || !newInvestor.email || !newInvestor.stage) {
    alert("Name, Email and Stage required");
    return;
    }

    await axios.post("/api/admin/investors", newInvestor, authHeader);
    setNewInvestor({
      name: "",
      email: "",
      stage: "",
      sector: "",
      ticketSize: "",
      region: "",
      website: "",
      matchScore: 80,
    });
    fetchInvestors();
  };

  /* =====================================================
     MENTORS
  ===================================================== */
  const fetchMentors = async () => {
    const res = await axios.get("/api/admin/mentors", authHeader);
    setMentors(
  Array.isArray(res.data)
    ? res.data
    : res.data?.mentors || []
  );
  };

  useEffect(() => {
    if (activeTab === "mentors") fetchMentors();
  }, [activeTab]);

  const saveRate = async (mentor) => {
    await axios.patch(
      `/api/admin/mentors/${mentor._id}/rate`,
      { hourlyRate: Number(newRate) },
      authHeader
    );
    setEditingRateId(null);
    fetchMentors();
  };

  const addMentor = async () => {
    if (!newMentor.name || !newMentor.email) {
      alert("Name & Email required");
      return;
    }

    await axios.post(
      "/api/admin/mentors",
      {
        ...newMentor,
        skills: newMentor.skills.split(",").map(s => s.trim()),
      },
      authHeader
    );

    setNewMentor({
      name: "",
      email: "",
      phone: "",
      skills: "",
      experience: "",
      bio: "",
      hourlyRate: "",
      status: "pending",
    });
    fetchMentors();
  };

  /* =====================================================
     PRO INTEREST
  ===================================================== */
  const fetchProInterests = async () => {
    const res = await axios.get("/api/admin/pro-interest", authHeader);
    setProInterests(
  Array.isArray(res.data)
    ? res.data
    : res.data?.proInterests || []
  );
  };

  useEffect(() => {
    if (activeTab === "pro-interest") fetchProInterests();
  }, [activeTab]);

  /* =====================================================
     UI
  ===================================================== */
  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold mb-4">👤 User Management</h2>

      {/* TABS */}
      <div className="mb-6">
        {["users", "investors", "mentors", "pro-interest"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 mr-2 ${
              activeTab === tab ? "bg-blue-600 text-white" : "bg-gray-200"
            }`}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ================= USERS ================= */}
      {activeTab === "users" && (
        <>
          {/* ADD USER */}
          <div className="border p-4 mb-4">
            <h3 className="font-semibold mb-2">➕ Add User (Admin)</h3>

            <input
              placeholder="Name"
              value={newUser.name}
              onChange={(e) =>
                setNewUser(prev => ({ ...prev, name: e.target.value }))
              }
            />
            <input
              placeholder="Email"
              value={newUser.email}
              onChange={(e) =>
                setNewUser(prev => ({ ...prev, email: e.target.value }))
              }
            />

            <select
              value={newUser.role}
              onChange={(e) =>
                setNewUser(prev => ({ ...prev, role: e.target.value }))
              }
            >
              <option value="founder">Founder</option>
              <option value="mentor">Mentor</option>
              <option value="investor">Investor</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>

            <select
              value={newUser.plan}
              onChange={(e) =>
              setNewUser(prev => ({ ...prev, plan: e.target.value }))
            }
            >
              <option value="free">Lite</option>
              <option value="pro">Pro</option>
            </select>

            <button
              className="bg-blue-600 text-white px-3 py-1 ml-2"
              onClick={addUser}
            >
              Create
            </button>
          </div>

          {/* USER TABLE */}
          <table className="w-full border">
            <thead className="bg-gray-100">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Plan</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
              {(Array.isArray(users) ? users : []).map(u => (
                <tr key={u._id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>

                  <td>
                    <select
                      value={u.role}
                      onChange={e => updateRole(u._id, e.target.value)}
                    >
                      <option value="founder">Founder</option>
                      <option value="mentor">Mentor</option>
                      <option value="investor">Investor</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  </td>

                  <td>
                    {/* 🔥 PLAN IS READ FROM users.plan ONLY */}
                    <select
                      value={u.plan || "free"}
                      onChange={e => updatePlan(u._id, e.target.value)}
                    >
                      <option value="free">Lite</option>
                      <option value="pro">Pro</option>
                    </select>
                  </td>

                  <td>
                    <button
                      className="text-red-600"
                      onClick={() => offboardUser(u._id)}
                    >
                      Offboard
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {activeTab === "investors" && (
  <div>
    <h3 className="font-semibold mb-2">📈 Investors</h3>

<div className="border p-4 mb-4">
  <h4 className="mb-2 font-medium">➕ Add Investor</h4>

  <input
    className="border px-2 py-1 mr-2"
    placeholder="Name"
    value={newInvestor.name}
    onChange={(e) =>
      setNewInvestor(prev => ({ ...prev, name: e.target.value }))
    }
  />

  <input
  className="border px-2 py-1 mr-2"
  placeholder="Email"
  value={newInvestor.email}
  onChange={(e) =>
    setNewInvestor(prev => ({ ...prev, email: e.target.value }))
  }
/>

  <input
    className="border px-2 py-1 mr-2"
    placeholder="Stage (Seed, Series A...)"
    value={newInvestor.stage}
    onChange={(e) =>
      setNewInvestor(prev => ({ ...prev, stage: e.target.value }))
    }
  />

  <input
    className="border px-2 py-1 mr-2"
    placeholder="Sector (Fintech, AI...)"
    value={newInvestor.sector}
    onChange={(e) =>
      setNewInvestor(prev => ({ ...prev, sector: e.target.value }))
    }
  />

  <button
    className="bg-blue-600 text-white px-3 py-1"
    onClick={addInvestor}
  >
    Create
  </button>
</div>

    <table className="w-full border">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Stage</th>
          <th>Sector</th>
        </tr>
      </thead>
      <tbody>
        {investors.length === 0 && (
          <tr>
            <td colSpan="4" className="text-center py-4 text-gray-500">
              No users found
            </td>
          </tr>
        )}
        {(Array.isArray(investors) ? investors : []).map(i => (
          <tr key={i._id}>
            <td>{i.name}</td>
            <td>{i.email || "-"}</td>
            <td>{i.stage}</td>
            <td>{i.sector}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}

{activeTab === "mentors" && (
  <div>
    <h3 className="font-semibold mb-2">🧑‍🏫 Mentors</h3>

<div className="border p-4 mb-4">
  <h4 className="mb-2 font-medium">➕ Add Mentor</h4>

  <input
    className="border px-2 py-1 mr-2"
    placeholder="Name"
    value={newMentor.name}
    onChange={(e) =>
      setNewMentor(prev => ({ ...prev, name: e.target.value }))
    }
  />

  <input
    className="border px-2 py-1 mr-2"
    placeholder="Email"
    value={newMentor.email}
    onChange={(e) =>
      setNewMentor(prev => ({ ...prev, email: e.target.value }))
    }
  />

  <input
    className="border px-2 py-1 mr-2"
    placeholder="Skills (comma separated)"
    value={newMentor.skills}
    onChange={(e) =>
      setNewMentor(prev => ({ ...prev, skills: e.target.value }))
    }
  />

  <button
    className="bg-blue-600 text-white px-3 py-1"
    onClick={addMentor}
  >
    Create
  </button>
</div>

    <table className="w-full border">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Skills</th>
        </tr>
      </thead>
      <tbody>
        {mentors.length === 0 && (
          <tr>
            <td colSpan="3" className="text-center py-4 text-gray-500">
              No users found
            </td>
          </tr>
        )}
        {(Array.isArray(mentors) ? mentors : []).map(m => (
          <tr key={m._id}>
            <td>{m.name}</td>
            <td>{m.email}</td>
            <td>{(m.skills || []).join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}

{/* ================= PRO INTEREST ================= */}
{activeTab === "pro-interest" && (
  <div>
    <h3 className="font-semibold mb-2">💰 Pro Upgrade Interest</h3>

    <table className="w-full border">
      <thead className="bg-gray-100">
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Startup</th>
          <th>Message</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody>
  {proInterests.length === 0 && (
    <tr>
      <td colSpan="5" className="text-center py-4 text-gray-500">
        No upgrade interest found
      </td>
    </tr>
  )}

  {(Array.isArray(proInterests) ? proInterests : []).map(p => (
    <tr key={p._id}>
      <td>{p.name || "-"}</td>
      <td>{p.email}</td>
      <td>{p.startup || "-"}</td>
      <td>{p.message || "-"}</td>
      <td>
        {p.createdAt
          ? new Date(p.createdAt).toLocaleDateString()
          : "-"}
      </td>
    </tr>
  ))}
</tbody>
    </table>

    
  </div>
)}
    </div>
  );
};

export default AdminUserManagement;
